import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building, Shield, Truck, Users, Heart, Hammer, Monitor, Plus, X, Upload, Trash2 } from 'lucide-react';
import logger from '../../utils/logger';
import DynamicFormModal from '../../components/Site/DynamicFormModal';
import useExtraSolutions from './useExtraSolutions';

// Badge "French Market Only" - visible uniquement en anglais
const FrenchMarketBadge = () => {
  const { i18n } = useTranslation();
  if (i18n.language?.startsWith('en') !== true) return null;
  
  return (
    <div className="absolute top-0 right-0 overflow-hidden w-32 h-32 z-10">
      <div className="absolute transform rotate-45 bg-red-600 text-white text-xs font-bold py-1.5 right-[-40px] top-[28px] w-[170px] text-center shadow-lg border-y-2 border-red-800">
        French market only
      </div>
    </div>
  );
};

// Mapping ID solution → slug Form Builder (slugs normalisés)
const SOLUTION_SLUGS = {
  'rc-pro': 'responsabilite-civile-professionnelle',
  'multirisque-pro': 'multirisque-professionnelle',
  'flotte-auto': 'flotte-auto-vehicules-pro',
  'mutuelle-pro': 'mutuelle-pro',
  'prevoyance-pro': 'prevoyance-pro',
  'decennale-btp': 'assurance-decennale-btp',
  'cyber-assurance': 'cyber-assurance',
  'autres-pro': 'demande-generale'
};

const SolutionsProfessionnels = () => {
  const { t } = useTranslation('public');
  const [selectedFormSlug, setSelectedFormSlug] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Solutions pour professionnels (8 cartes)
  const solutions = [
    {
      id: 'rc-pro',
      title: t('solutions.professionals.rcPro.title'),
      description: t('solutions.professionals.rcPro.description'),
      icon: Shield,
      color: 'text-blue-600'
    },
    {
      id: 'multirisque-pro',
      title: t('solutions.professionals.multirisque.title'),
      description: t('solutions.professionals.multirisque.description'),
      icon: Building,
      color: 'text-green-600'
    },
    {
      id: 'flotte-auto',
      title: t('solutions.professionals.fleet.title'),
      description: t('solutions.professionals.fleet.description'),
      icon: Truck,
      color: 'text-orange-600'
    },
    {
      id: 'mutuelle-pro',
      title: t('solutions.professionals.healthPro.title'),
      description: t('solutions.professionals.healthPro.description'),
      icon: Heart,
      color: 'text-red-600'
    },
    {
      id: 'prevoyance-pro',
      title: t('solutions.professionals.lifePro.title'),
      description: t('solutions.professionals.lifePro.description'),
      icon: Users,
      color: 'text-purple-600'
    },
    {
      id: 'decennale-btp',
      title: t('solutions.professionals.decennial.title'),
      description: t('solutions.professionals.decennial.description'),
      icon: Hammer,
      color: 'text-yellow-600'
    },
    {
      id: 'cyber-assurance',
      title: t('solutions.professionals.cyber.title'),
      description: t('solutions.professionals.cyber.description'),
      icon: Monitor,
      color: 'text-indigo-600'
    },
    {
      id: 'autres-pro',
      title: t('solutions.professionals.other.title'),
      description: t('solutions.professionals.other.description'),
      icon: Plus,
      color: 'text-gray-600'
    }
  ];

  // Ouvrir le formulaire dynamique
  const openDevisModal = (slug) => {
    setSelectedFormSlug(slug || 'demande-generale');
  };

  const ICON_BY_SLUG = {};
  solutions.forEach(s => { const slug = SOLUTION_SLUGS[s.id]; if (slug) ICON_BY_SLUG[slug] = { icon: s.icon, color: s.color }; });
  const dbSolutions = useExtraSolutions('professionnels', ICON_BY_SLUG);
  const allSolutions = dbSolutions.length ? dbSolutions : solutions;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-brand-primary to-brand-primary-hover text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
            {t('solutions.professionals.pageTitle')}
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto">
            {t('solutions.professionals.pageSubtitle')}
          </p>
        </div>
      </section>

      {/* Cards Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {allSolutions.map((solution) => {
              const IconComponent = solution.icon;
              return (
                <div
                  key={solution.id}
                  className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden flex flex-col h-full relative"
                >
                  {/* Badge French Market - visible en anglais */}
                  <FrenchMarketBadge />
                  
                  <div className="p-6 md:p-8 flex flex-col flex-grow">
                    {/* Icon */}
                    <div className="flex justify-center mb-6">
                      <div className="bg-gray-50 group-hover:bg-brand-primary/10 p-4 rounded-full transition-all duration-300">
                        <IconComponent 
                          size={48} 
                          className={`${solution.color} group-hover:text-brand-primary transition-colors duration-300`}
                        />
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-brand-text group-hover:text-brand-primary transition-colors duration-300 mb-4 text-center">
                      {solution.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-center mb-8 leading-relaxed text-sm md:text-base flex-grow">
                      {solution.description}
                    </p>

                    {/* CTA Button */}
                    <div className="text-center mt-auto">
                      <button
                        onClick={() => openDevisModal(solution.slug || SOLUTION_SLUGS[solution.id])}
                        className="w-full bg-gray-100 text-brand-text px-6 py-3 rounded-lg font-semibold hover:bg-brand-primary hover:text-white transition-all duration-300 hover:shadow-lg transform hover:scale-105"
                      >
                        {t('solutions.requestStudy')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-brand-primary to-brand-primary-hover py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            {t('solutions.professionals.ctaTitle')}
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-6 md:mb-8">
            {t('solutions.professionals.ctaSubtitle')}
          </p>
          <button
            onClick={() => setSelectedFormSlug('demande-generale')}
            className="bg-white text-brand-primary px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold text-base md:text-lg hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 transform hover:-translate-y-1"
          >
            {t('solutions.freeStudyRequest')}
          </button>
        </div>
      </section>

      {/* Modal Devis */}

      {/* Modal Formulaire Dynamique */}
      {selectedFormSlug && (
        <DynamicFormModal
          slug={selectedFormSlug}
          onClose={() => setSelectedFormSlug(null)}
          backendUrl={backendUrl}
        />
      )}
    </div>
  );
};

export default SolutionsProfessionnels;
